import { useEffect, useRef, useState } from 'react';

import { Badge, Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { mdiClockOutline, mdiPhone, mdiPhoneInTalk, mdiPhoneOff } from '@mdi/js';
import { Icon } from '@mdi/react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Deal } from '../../gql/dealGql';
import { stripDeprecatedFinancialInfoFields } from '../../gql/financialInfoGql';
import {
  NewEstimateCustomerInput,
  NewEstimateUpsertInput,
  useCallOutMutation,
  useDuplicateDealsLazyQuery,
  useHandleThunderOutboundCallOutcomeMutation,
  useNewEstimateUpsertMutation,
} from '../../gql/generated/graphql';

import Modal from '../shared/Modal';

import { ThunderOutboundCallModalData } from './ThunderOutboundCallModalManager';

import { logger } from '../../libs/Logger';
import {
  formatPhoneNumberForDisplay,
  formatPhoneWithParentheses,
  formatTime,
} from '../../libs/utils';
import { cleanPhoneNumber } from '../../utils/phoneNumbers';

const COUNTDOWN_DURATION = 60; // seconds
const COUNTDOWN_STORAGE_KEY = 'thunderOutboundCallCountdownStart';
const DIALING_DURATION = 5000; // milliseconds
const MODAL_STATE_STORAGE_KEY = 'thunderOutboundCallModalState';
const DIALING_START_STORAGE_KEY = 'thunderOutboundCallDialingStart';

const MODAL_STATE_COUNTDOWN = 'countdown' as const;
const MODAL_STATE_DIALING = 'dialing' as const;
const MODAL_STATE_CALL_IN_PROGRESS = 'call-in-progress' as const;

type ModalState =
  | typeof MODAL_STATE_COUNTDOWN
  | typeof MODAL_STATE_DIALING
  | typeof MODAL_STATE_CALL_IN_PROGRESS;

const saveModalState = (state: ModalState) => {
  localStorage.setItem(MODAL_STATE_STORAGE_KEY, state);
  if (state === MODAL_STATE_DIALING) {
    localStorage.setItem(DIALING_START_STORAGE_KEY, Date.now().toString());
  } else if (state === MODAL_STATE_CALL_IN_PROGRESS) {
    localStorage.removeItem(DIALING_START_STORAGE_KEY);
  }
};

const loadModalState = (): ModalState => {
  const storedState = localStorage.getItem(MODAL_STATE_STORAGE_KEY);
  if (!storedState) {
    return MODAL_STATE_COUNTDOWN;
  }

  // If state is dialing, check if 5 seconds have passed
  if (storedState === MODAL_STATE_DIALING) {
    const dialingStart = localStorage.getItem(DIALING_START_STORAGE_KEY);
    if (dialingStart) {
      const elapsed = Date.now() - parseInt(dialingStart, 10);
      if (elapsed >= DIALING_DURATION) {
        // Transition to call-in-progress if enough time has passed
        return MODAL_STATE_CALL_IN_PROGRESS;
      }
    }
  }

  // Validate the stored state
  if (
    storedState === MODAL_STATE_COUNTDOWN ||
    storedState === MODAL_STATE_DIALING ||
    storedState === MODAL_STATE_CALL_IN_PROGRESS
  ) {
    return storedState as ModalState;
  }

  return MODAL_STATE_COUNTDOWN;
};

const clearModalStateStorage = () => {
  localStorage.removeItem(MODAL_STATE_STORAGE_KEY);
  localStorage.removeItem(DIALING_START_STORAGE_KEY);
};

type ThunderOutboundCallModalProps = {
  isOpen: boolean;
  onClose: () => void;
  leadData: ThunderOutboundCallModalData | null;
  onDuplicatesFound?: (deals: Deal[]) => void;
};

// The inbound-callback variant lives in ThunderInboundCallbackModal — the flows diverge
// enough (no countdown, no outcome reporting, dismissible) that two focused components
// read better than one branched component.
const ThunderOutboundCallModal = ({
  isOpen,
  onClose,
  leadData,
  onDuplicatesFound,
}: ThunderOutboundCallModalProps) => {
  const history = useHistory();
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);
  const [modalState, setModalState] = useState<ModalState>(() => loadModalState());
  const [callOutbound] = useCallOutMutation();
  const [handleThunderOutboundCallOutcome, { loading: isHandleThunderOutboundCallOutcomeLoading }] =
    useHandleThunderOutboundCallOutcomeMutation();
  const [newEstimateUpsert, { loading: isNewEstimateUpsertLoading }] =
    useNewEstimateUpsertMutation();

  const [checkDuplicateDeals, { loading: duplicateDealsLoading }] = useDuplicateDealsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const isProcessingOutcome =
    isHandleThunderOutboundCallOutcomeLoading ||
    isNewEstimateUpsertLoading ||
    duplicateDealsLoading;
  const hasDialedRef = useRef(false);
  const hasProcessedOutcomeRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dialingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setModalStateWithPersistence = (state: ModalState) => {
    setModalState(state);
    saveModalState(state);
  };

  const clearCountdownStorage = () => {
    localStorage.removeItem(COUNTDOWN_STORAGE_KEY);
  };

  const getRemainingTime = (): number => {
    const storedStartTime = localStorage.getItem(COUNTDOWN_STORAGE_KEY);
    if (!storedStartTime) {
      return COUNTDOWN_DURATION;
    }
    const startTime = parseInt(storedStartTime, 10);
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const remaining = COUNTDOWN_DURATION - elapsed;
    return Math.max(0, remaining);
  };

  const dialPhoneNumber = async (phoneNum?: string) => {
    if (hasDialedRef.current) {
      return;
    }
    hasDialedRef.current = true;

    const phoneNumber = phoneNum ?? leadData?.phoneNumber;
    if (!phoneNumber) {
      toast.error('No phone number available');
      hasDialedRef.current = false;
      return;
    }

    const callOutTo = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
    if (!callOutTo || callOutTo.length !== 10) {
      toast.error('Invalid phone number');
      hasDialedRef.current = false;
      return;
    }

    try {
      clearCountdownStorage();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      await callOutbound({
        variables: {
          calledNumber: callOutTo,
          ...(leadData?.thunderPhoneNumber && {
            thunderCallerIdOverride: leadData.thunderPhoneNumber,
          }),
        },
      });

      toast.success(`Calling ${formatPhoneNumberForDisplay(callOutTo)}`);
      setModalStateWithPersistence(MODAL_STATE_DIALING);
    } catch (error) {
      toast.error('Failed to place outbound call');
      logger.error('ThunderOutboundCallModal.tsx', 'Error placing outbound call:', null, error);
      hasDialedRef.current = false;
      setModalStateWithPersistence(MODAL_STATE_COUNTDOWN);
    }
  };

  const handleAction = async () => {
    await dialPhoneNumber();
  };

  useEffect(() => {
    if (modalState === MODAL_STATE_DIALING) {
      dialingTimeoutRef.current = setTimeout(() => {
        setModalStateWithPersistence(MODAL_STATE_CALL_IN_PROGRESS);
      }, DIALING_DURATION);
    }

    return () => {
      if (dialingTimeoutRef.current) {
        clearTimeout(dialingTimeoutRef.current);
        dialingTimeoutRef.current = null;
      }
    };
  }, [modalState]);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(COUNTDOWN_DURATION);
      setModalStateWithPersistence(MODAL_STATE_COUNTDOWN);
      clearCountdownStorage();
      clearModalStateStorage();
      hasDialedRef.current = false;
      hasProcessedOutcomeRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (dialingTimeoutRef.current) {
        clearTimeout(dialingTimeoutRef.current);
        dialingTimeoutRef.current = null;
      }
      return;
    }

    hasDialedRef.current = false;
    hasProcessedOutcomeRef.current = false;

    const loadedState = loadModalState();
    setModalState(loadedState);

    if (loadedState === MODAL_STATE_COUNTDOWN) {
      const remainingTime = getRemainingTime();
      setCountdown(remainingTime);

      if (!localStorage.getItem(COUNTDOWN_STORAGE_KEY)) {
        localStorage.setItem(COUNTDOWN_STORAGE_KEY, Date.now().toString());
      }

      if (remainingTime <= 0) {
        dialPhoneNumber();
        return;
      }

      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            clearCountdownStorage();
            dialPhoneNumber();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (loadedState === MODAL_STATE_DIALING) {
      const dialingStart = localStorage.getItem(DIALING_START_STORAGE_KEY);
      if (dialingStart) {
        const elapsed = Date.now() - parseInt(dialingStart, 10);
        const remaining = DIALING_DURATION - elapsed;
        if (remaining > 0) {
          dialingTimeoutRef.current = setTimeout(() => {
            setModalStateWithPersistence(MODAL_STATE_CALL_IN_PROGRESS);
          }, remaining);
        } else {
          setModalStateWithPersistence(MODAL_STATE_CALL_IN_PROGRESS);
        }
      }
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (dialingTimeoutRef.current) {
        clearTimeout(dialingTimeoutRef.current);
        dialingTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const scheduledTime = leadData?.scheduledTime;
  const scheduledTimeFormatted = scheduledTime ? formatTime(scheduledTime) : null;

  const handleCallOutcome = async (answered: boolean) => {
    if (hasProcessedOutcomeRef.current) {
      return;
    }
    hasProcessedOutcomeRef.current = true;

    if (!leadData?.phoneNumber) {
      onClose();
      return;
    }

    const cleanedNumber = cleanPhoneNumber(leadData.phoneNumber);
    if (!cleanedNumber) {
      toast.error('Invalid phone number');
      onClose();
      return;
    }

    let dealId: number | null = null;

    try {
      if (answered) {
        let dupData;
        let dupError;
        try {
          const result = await checkDuplicateDeals({
            variables: {
              phoneNumber: formatPhoneWithParentheses(cleanedNumber),
              matchBootedDeals: true,
            },
          });
          dupData = result.data;
          dupError = result.error;
        } catch (networkError) {
          logger.error(
            'ThunderOutboundCallModal.tsx',
            'Network error checking for duplicates:',
            null,
            networkError,
          );
          toast.error('Failed to check for duplicates');
        }

        if (dupError) {
          toast.error('Failed to check for duplicates');
          // Fall through to create deal
        } else if (dupData?.duplicateDeals?.length) {
          const firstDuplicateDealId = dupData.duplicateDeals[0]?.id ?? null;

          try {
            await handleThunderOutboundCallOutcome({
              variables: {
                calledNumber: cleanedNumber,
                answered,
                attemptNum: leadData.attemptNum,
                thunderCallNotificationId: leadData.thunderCallNotificationId,
                dealId: firstDuplicateDealId,
              },
            });
          } catch (error) {
            logger.error(
              'ThunderOutboundCallModal.tsx',
              'Error updating call notification:',
              null,
              error,
            );
            toast.error('Failed to update call notification');
          }

          onClose();
          if (onDuplicatesFound) {
            const validDeals = dupData.duplicateDeals.filter(
              (deal): deal is NonNullable<typeof deal> => deal !== null,
            );
            onDuplicatesFound(validDeals as Deal[]);
          }
          return;
        }

        // No duplicates (or dup check failed and we're falling through): create a fresh estimate.
        const fullname = leadData?.fullname ?? '';
        const nameParts = fullname.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const estimate = new Deal();
        estimate.customer.first_name = firstName;
        estimate.customer.last_name = lastName;
        estimate.customer.phone_number = formatPhoneWithParentheses(cleanedNumber);

        const customerInput: NewEstimateCustomerInput = {
          first_name: estimate.customer.first_name,
          last_name: estimate.customer.last_name,
          phone_number: estimate.customer.phone_number,
          employment: {},
          address: { ...estimate.customer.address },
        };

        const estimateInput: NewEstimateUpsertInput = {
          customer: customerInput,
          car: {
            vin: estimate.car.vin,
            year: estimate.car.year,
            make: estimate.car.make,
            model: estimate.car.model,
            payoff: { ...estimate.car.payoff },
          },
          financial_info: stripDeprecatedFinancialInfoFields({ ...estimate.financial_info }),
          type: estimate.type,
          source: estimate.source,
          state: estimate.state,
          notes: [{ text: 'Thunder Outbound Call Created' }],
        };

        const estimateResult = await newEstimateUpsert({
          variables: { newEstimateUpsertInput: estimateInput },
        });

        dealId = estimateResult.data?.newEstimateUpsert?.id ?? null;
      }
    } catch (error) {
      logger.error('ThunderOutboundCallModal.tsx', 'Error creating estimate:', null, error);
      toast.error('Failed to create estimate');
    }

    try {
      await handleThunderOutboundCallOutcome({
        variables: {
          calledNumber: cleanedNumber,
          answered,
          attemptNum: leadData.attemptNum,
          thunderCallNotificationId: leadData.thunderCallNotificationId,
          dealId,
        },
      });
    } catch (error) {
      logger.error(
        'ThunderOutboundCallModal.tsx',
        'Error updating call notification:',
        null,
        error,
      );
      toast.error('Failed to update call notification');
    }

    if (dealId) {
      history.push(`/deals/${dealId}`);
    }
    onClose();
  };

  const renderLeftButtons = () => {
    if (modalState === MODAL_STATE_COUNTDOWN) {
      return (
        <HStack spacing={2} alignItems="center">
          <Icon path={mdiClockOutline} size="20px" color="gray.700" />
          <Text fontSize="sm" color="gray.700" fontWeight="normal">
            Auto-dial in {countdown}s
          </Text>
        </HStack>
      );
    }
    return null;
  };

  const renderRightButtons = () => {
    if (modalState === MODAL_STATE_COUNTDOWN) {
      return (
        <Button
          leftIcon={<Icon path={mdiPhone} size="20px" />}
          variant="primary"
          onClick={handleAction}
          textTransform="uppercase"
        >
          Dial When Ready
        </Button>
      );
    }
    if (modalState === MODAL_STATE_DIALING) {
      return (
        <HStack spacing={2} alignItems="center">
          <Icon path={mdiPhone} size="20px" color="gray.700" />
          <Text fontSize="sm" color="gray.700" fontWeight="normal">
            Dialing...
          </Text>
        </HStack>
      );
    }
    if (modalState === MODAL_STATE_CALL_IN_PROGRESS) {
      return (
        <HStack spacing={2}>
          <Button
            leftIcon={<Icon path={mdiPhoneInTalk} size="20px" />}
            variant="primary"
            onClick={() => handleCallOutcome(true)}
            textTransform="uppercase"
            isLoading={isProcessingOutcome}
            isDisabled={isProcessingOutcome}
          >
            Customer Answered
          </Button>
          <Button
            leftIcon={<Icon path={mdiPhoneOff} size="20px" />}
            variant="secondary"
            onClick={() => handleCallOutcome(false)}
            textTransform="uppercase"
            isLoading={isProcessingOutcome}
            isDisabled={isProcessingOutcome}
          >
            No Answer
          </Button>
        </HStack>
      );
    }
    return null;
  };

  return (
    <Modal
      title={leadData?.fullname ? `Call Briefing: ${leadData.fullname}` : 'Call Briefing'}
      isOpen={isOpen}
      onClose={onClose}
      canDismiss={false}
      closeOnOverlayClick={false}
      size="lg"
      leftButtons={renderLeftButtons()}
      rightButtons={renderRightButtons()}
    >
      <VStack spacing={4} align="stretch">
        <Flex justifyContent="flex-start">
          <Badge
            role="status"
            bg="blue.100"
            color="blue.800"
            fontSize="xs"
            textTransform="uppercase"
            px={2}
            py={1}
            borderRadius="md"
          >
            ALEX (AI) INITIATED CALL
          </Badge>
        </Flex>

        {leadData?.aiCallOpener && (
          <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={4}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="blue.600"
              textTransform="uppercase"
              mb={2}
            >
              Opener
            </Text>
            <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
              {leadData.aiCallOpener}
            </Text>
          </Box>
        )}

        <VStack spacing={1} align="flex-start">
          {leadData?.phoneNumber && (
            <HStack spacing={2} alignItems="center">
              {leadData?.thunderPhoneNumber && (
                <Text fontSize="sm" color="gray.600">
                  Calling
                </Text>
              )}
              <Text fontWeight="bold" fontSize="sm">
                {formatPhoneNumberForDisplay(leadData.phoneNumber)}
              </Text>
              {leadData?.thunderPhoneNumber && (
                <>
                  <Text fontSize="sm" color="gray.600">
                    from
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {formatPhoneNumberForDisplay(leadData.thunderPhoneNumber)}
                  </Text>
                </>
              )}
            </HStack>
          )}
          {scheduledTimeFormatted && (
            <Text fontSize="sm" color="gray.600">
              • Scheduled: {scheduledTimeFormatted}
            </Text>
          )}
        </VStack>
      </VStack>
    </Modal>
  );
};

export default ThunderOutboundCallModal;
